import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import { Copy, Key, Loader2, Plus, ShieldOff } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";

interface KeyRow {
  id: string;
  name: string;
  key_prefix: string;
  last_used_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

export default function Developers() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [keys, setKeys] = useState<KeyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [minting, setMinting] = useState(false);
  const [freshKey, setFreshKey] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading && !user) navigate("/auth?redirect=/developers");
  }, [authLoading, user, navigate]);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("intel_api_keys")
      .select("id,name,key_prefix,last_used_at,revoked_at,created_at")
      .order("created_at", { ascending: false });
    if (error) {
      toast({ title: "Could not load keys", description: error.message, variant: "destructive" });
    } else {
      setKeys((data ?? []) as KeyRow[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (user) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const mint = async () => {
    setMinting(true);
    setFreshKey(null);
    try {
      const { data, error } = await supabase.functions.invoke("intel-mint-api-key", {
        body: { action: "mint", name: name.trim() || "API key" },
      });
      if (error) throw error;
      const d = data as { ok?: boolean; key?: string; error?: string };
      if (d?.error === "intelligence_tier_required") {
        toast({
          title: "Intelligence tier required",
          description: "Contact us at info@emrkt.co to upgrade.",
          variant: "destructive",
        });
        return;
      }
      if (!d?.ok || !d?.key) throw new Error(d?.error ?? "Mint failed");
      setFreshKey(d.key);
      setName("");
      load();
    } catch (err) {
      toast({
        title: "Could not mint key",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    } finally {
      setMinting(false);
    }
  };

  const revoke = async (id: string) => {
    if (!confirm("Revoke this API key? Any client using it will start getting 401 immediately.")) return;
    const { error } = await supabase.functions.invoke("intel-mint-api-key", {
      body: { action: "revoke", id },
    });
    if (error) {
      toast({ title: "Could not revoke", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Key revoked" });
    load();
  };

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: "Copied" });
    } catch {
      toast({ title: "Copy failed", variant: "destructive" });
    }
  };

  const aggregatesBase = "https://ypfplfhmimgwjtvlswpw.supabase.co/functions/v1/intel-aggregates-api";
  const publicApiBase = "https://ypfplfhmimgwjtvlswpw.supabase.co/functions/v1/public-api";

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Developers & API — The Emerging Market</title>
        <meta name="description" content="Public read-only API for Alabama civic data — legislation, grants, funders, ideas — plus Intelligence-tier aggregates. Mint API keys and integrate emrkt.co data into your product." />
        <link rel="canonical" href="https://emrkt.co/developers" />
      </Helmet>
      <SiteHeader />
      <main className="container max-w-4xl mx-auto px-4 py-12 md:py-16">
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-2">
            <Key className="h-5 w-5 text-primary" />
            <span className="text-xs uppercase tracking-widest text-primary font-semibold">Developers</span>
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground">emrkt.co API</h1>
          <p className="mt-3 text-muted-foreground text-lg">
            Read-only access to Alabama civic data — legislation, grant programs, funders, and
            community ideas — plus Intelligence-tier anonymized aggregates. Attribution
            <span className="italic"> "The Emerging Market — emrkt.co"</span> is required in any
            downstream display. See the{" "}
            <Link to="/methodology" className="text-primary underline">methodology</Link>{" "}
            for source and cadence details, and the{" "}
            <a href="/openapi.json" className="text-primary underline">OpenAPI 3.1 spec</a>{" "}
            to auto-generate a client.
          </p>
        </div>

        <section className="rounded-2xl border border-border bg-card p-6 mb-10">
          <h2 className="font-display text-lg font-semibold mb-2">Mint a key</h2>
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
            <div className="flex-1 w-full">
              <Label htmlFor="key-name">Label</Label>
              <Input id="key-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Grants dashboard — production" />
            </div>
            <Button onClick={mint} disabled={minting}>
              {minting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Mint key
            </Button>
          </div>
          {freshKey && (
            <div className="mt-4 rounded-xl border border-primary/30 bg-primary/5 p-4">
              <p className="text-xs uppercase tracking-wider text-primary font-semibold mb-1">Copy now — shown once</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 break-all text-xs bg-background rounded px-2 py-1 border border-border">{freshKey}</code>
                <Button size="icon" variant="outline" onClick={() => copy(freshKey)} aria-label="Copy key">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </section>

        <section className="mb-10">
          <h2 className="font-display text-lg font-semibold mb-4">Your keys</h2>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : keys.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border p-6 text-sm text-muted-foreground">
              No keys yet. Mint one above.
            </div>
          ) : (
            <ul className="space-y-2">
              {keys.map((k) => (
                <li key={k.id} className="rounded-xl border border-border bg-card p-4 flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-foreground">{k.name}</div>
                    <div className="text-xs text-muted-foreground font-mono">
                      {k.key_prefix}… · created {new Date(k.created_at).toLocaleDateString()}
                      {k.last_used_at && ` · last used ${new Date(k.last_used_at).toLocaleDateString()}`}
                      {k.revoked_at && ` · revoked ${new Date(k.revoked_at).toLocaleDateString()}`}
                    </div>
                  </div>
                  {!k.revoked_at && (
                    <Button size="sm" variant="outline" onClick={() => revoke(k.id)}>
                      <ShieldOff className="h-3 w-3 mr-1" /> Revoke
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="rounded-2xl border border-border bg-card p-6 space-y-4 mb-6">
          <div>
            <h2 className="font-display text-lg font-semibold">Public data API</h2>
            <p className="text-xs text-muted-foreground mt-1">
              Free tier: 60 req/min · Intelligence tier: 600 req/min
            </p>
          </div>
          <p className="text-sm text-muted-foreground">
            Pass the key as <code>X-Emrkt-Key</code>. Every response includes
            <code> X-Attribution</code>. List endpoints paginate with an opaque
            <code> cursor</code> — pass <code>next_cursor</code> back in <code>?cursor=</code>.
          </p>
          <pre className="text-xs bg-background rounded p-3 border border-border overflow-x-auto"><code>{`curl -H "X-Emrkt-Key: emrkt_..." \\
  "${publicApiBase}/v1/legislation?county=Mobile&area=Health%20%26%20Wellness&limit=25"`}</code></pre>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li><code>GET /v1/legislation</code> — filters: <code>county</code>, <code>area</code>, <code>status</code>, <code>jurisdiction</code>, <code>updated_since</code></li>
            <li><code>GET /v1/legislation/{`{bill_id}`}</code></li>
            <li><code>GET /v1/grants</code> — filters: <code>focus_area</code>, <code>deadline_before</code>, <code>award_min/max</code>, <code>verified</code>, <code>opportunity_type</code></li>
            <li><code>GET /v1/grants/{`{id}`}</code></li>
            <li><code>GET /v1/funders</code> — filters: <code>focus_area</code>, <code>hq_state</code>, <code>funder_type</code>, <code>verified</code></li>
            <li><code>GET /v1/funders/{`{slug}`}</code></li>
            <li><code>GET /v1/ideas</code> — filters: <code>category</code>, <code>featured</code> (published only)</li>
            <li><code>GET /v1/ideas/{`{id}`}</code></li>
          </ul>
        </section>

        <section className="rounded-2xl border border-border bg-card p-6 space-y-4 mb-6">
          <h2 className="font-display text-lg font-semibold">Intelligence aggregates</h2>
          <p className="text-sm text-muted-foreground">
            Anonymized, no-PII rollups. Intelligence-tier keys only. Same
            <code> X-Emrkt-Key</code> auth (also accepts <code>X-API-Key</code>).
          </p>
          <pre className="text-xs bg-background rounded p-3 border border-border overflow-x-auto"><code>{`curl -H "X-Emrkt-Key: emrkt_..." \\
  "${aggregatesBase}/dbe-counts?sector=Health%20%26%20Wellness"`}</code></pre>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li><code>GET /dbe-counts?sector=&amp;county=</code> — active partner + affiliate counts by tier</li>
            <li><code>GET /grant-fit-distribution</code> — grant programs bucketed by focus area</li>
            <li><code>GET /idea-pipeline-volume?county=</code> — published ideas total, last 30d, and by category</li>
            <li><code>GET /opportunity-mix?verified=</code> — opportunity type × provider matrix</li>
          </ul>
        </section>

        <section className="rounded-2xl border border-primary/20 bg-primary/5 p-6 space-y-2">
          <h3 className="font-display text-sm font-semibold">Attribution & fair use</h3>
          <p className="text-xs text-muted-foreground">
            By using the API you agree to display <span className="italic">"Data: The Emerging Market — emrkt.co"</span>
            {" "}near any surfaced field, link back on public pages, and follow the{" "}
            <Link to="/terms" className="text-primary underline">Terms of Service</Link>.
            Any breaking change is announced on the{" "}
            <Link to="/changelog" className="text-primary underline">changelog</Link>.
          </p>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}